import { NgClass, NgOptimizedImage } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  forwardRef,
  inject,
  input,
} from '@angular/core'
import type { ControlValueAccessor } from '@angular/forms'
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'

@Component({
  selector: 'dhp-numerical-input',
  templateUrl: './numerical-input.component.html',
  styleUrls: ['./numerical-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericalInputComponent),
      multi: true,
    },
  ],
  imports: [NgClass, NgOptimizedImage, FormsModule],
})
export class NumericalInputComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef)

  min = input<number>()
  max = input<number>()
  minSelected = input<number>()

  value = 0
  isDisabled = false

  constructor() {
    effect(() => {
      this.min()
      this.max()
      this.minSelected()

      const normalizedValue = this.validateValue(this.value)

      if (normalizedValue !== this.value) {
        this.value = normalizedValue
        this.onChange(this.value)
      }

      this.cdr.markForCheck()
    })
  }

  increment(): void {
    if (this.hasMinSelected() && this.value <= 0) {
      this.setValue(this.minSelectedValue())

      return
    }

    this.setValue(this.value + 1)
  }

  decrement(): void {
    if (this.hasMinSelected() && this.value === this.minSelectedValue()) {
      this.setValue(0)

      return
    }

    this.setValue(this.value - 1)
  }

  registerOnChange(fn: CallbackFunction): void {
    this.onChange = fn
  }

  registerOnTouched(fn: CallbackFunction): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled
  }

  writeValue(value: number | null): void {
    this.value = this.validateValue(value)
  }

  onValueChange(value: number | string | null): void {
    this.setValue(value ?? 0)
  }

  minValue(): number {
    return this.min() ?? 0
  }

  maxValue(): number {
    return this.max() ?? Infinity
  }

  minSelectedValue(): number {
    const configuredMinSelected = this.minSelected() ?? 0

    return Math.min(Math.max(configuredMinSelected, 0), this.maxValue())
  }

  onBlur(event: Event): void {
    const target = event.target as HTMLInputElement
    this.setValue(target.value)
    target.value = this.value.toString()
  }

  protected setValue(value: number | string): void {
    this.value = this.validateValue(value)

    this.onChange(this.value)
    this.onTouched()
    this.cdr.markForCheck()
  }

  private onChange: CallbackFunction = noopCallback

  private onTouched: CallbackFunction = noopCallback

  private hasMinSelected(): boolean {
    return this.minValue() <= 0 && this.minSelectedValue() > 0
  }

  private validateValue(value: number | string | null): number {
    const parsedValue = parseInt(value?.toString() ?? '')
    let validValue = isNaN(parsedValue) ? 0 : parsedValue

    if (validValue < this.minValue()) {
      validValue = this.minValue()
    }
    if (validValue > this.maxValue()) {
      validValue = this.maxValue()
    }
    if (
      this.hasMinSelected() &&
      validValue > 0 &&
      validValue < this.minSelectedValue()
    ) {
      validValue = this.minSelectedValue()
    }

    return validValue
  }
}

type CallbackFunction = (value?: number) => void

const noopCallback: CallbackFunction = () => undefined
